import React from 'react';
import FormInput from '../../../../../components/FormInput';

// Must mirror the backend `UseCase` choices (api/users/models/user_resources.py).
const USE_CASES = [
  ['all', 'All'],
  ['solar_power', 'Solar'],
  ['wind_power', 'Wind'],
  ['mfrr_price', 'mFRR Price'],
  ['mfrr_energy', 'mFRR Energy'],
];

const SORTS = [
  ['name', 'Name'],
  ['registered_at', 'Registration Date'],
];

const OWNERSHIP = [
  ['all', 'All'],
  ['mine', 'Owned'],
  ['others', 'Others'],
];

const selectClass = 'block rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 px-2 py-1.5 text-xs text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 cursor-pointer';

// Pill-shaped control for the phone filter strip: reads as a chip, behaves as
// whatever element it wraps.
const chipClass = (active) => `flex-shrink-0 inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs whitespace-nowrap ${
  active
    ? 'border-brand-500 bg-brand-50 text-brand-700 font-semibold dark:bg-brand-900/30 dark:text-brand-300'
    : 'border-gray-200/70 bg-surface text-gray-700 dark:border-gray-600/70 dark:bg-gray-870 dark:text-gray-300'
}`;

export default function ResourceListHeader({
  search, setSearch, useCaseFilter, setUseCaseFilter, sortBy, setSortBy, searchRef,
  filterMode, setFilterMode, hideInactive, setHideInactive,
}) {
  return (
    <div className="p-3 lg:p-6 border-b border-gray-100 dark:border-gray-700">
      {/* Desktop header: title, subtitle and the two labelled selects. */}
      <div className="hidden lg:flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Resource List</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Browse and manage your resources.</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="resource-type-filter" className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Type:</label>
            <select id="resource-type-filter" value={useCaseFilter} onChange={(e) => setUseCaseFilter(e.target.value)} className={selectClass}>
              {USE_CASES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="resource-sort" className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Sort:</label>
            <select id="resource-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={selectClass}>
              {SORTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="lg:mt-4">
        <FormInput ref={searchRef} leftIcon="search" size="large" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, id or email" aria-label="Search resources" />
      </div>

      {/* Phone strip: the filters that live in the sidebar on desktop, kept
          above the list so they are reachable without scrolling past it. */}
      <div className="relative mt-2.5 flex gap-1.5 overflow-x-auto pb-0.5 lg:hidden">
        {OWNERSHIP.map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            onClick={() => setFilterMode(mode)}
            aria-pressed={filterMode === mode}
            className={chipClass(filterMode === mode)}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setHideInactive(!hideInactive)}
          aria-pressed={!!hideInactive}
          className={chipClass(!!hideInactive)}
        >
          {hideInactive && (
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-3 w-3">
              <path d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.6 7.6a1 1 0 0 1-1.4 0L3.3 9.9a1 1 0 1 1 1.4-1.4l3 3 6.9-6.9a1 1 0 0 1 1.4 0z"/>
            </svg>
          )}
          Hide deactivated
        </button>
        <label className={`${chipClass(useCaseFilter !== 'all')} cursor-pointer`}>
          <span className="sr-only">Filter by type</span>
          <select
            value={useCaseFilter}
            onChange={(e) => setUseCaseFilter(e.target.value)}
            className="bg-transparent text-xs focus:outline-none"
          >
            {USE_CASES.map(([value, label]) => (
              <option key={value} value={value}>{value === 'all' ? 'Type: all' : label}</option>
            ))}
          </select>
        </label>
        <label className={`${chipClass(sortBy !== 'name')} cursor-pointer`}>
          <span className="sr-only">Sort by</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-transparent text-xs focus:outline-none"
          >
            {SORTS.map(([value, label]) => (
              <option key={value} value={value}>{value === 'name' ? 'Sort: name' : 'Sort: date'}</option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}
